<template>
  <common-modal
    :name="name"
    :title="title"
    :hideFooter="true"
    :onClosed="handleClose"
    height="auto"
    :width="generateWidth"
  >
    <template #modal-body>
      <div class="pl-2 pt-5">
        <p>{{ $t("class.weighting_method") }}</p>
        <md-switch v-model="isSwitchMode">
          <span class="text-[#242424]">
            {{ $t("class.extra_coursework_actitivities") }}</span
          ></md-switch
        >
        <p class="text-sm text-[#6C757D]">
          {{ $t("class.method_to_calculate") }}
        </p>
        <validation-form @on-submit="saveParameters">
          <div v-if="isSwitchMode">
            <div class="flex pb-2">
              <div class="flex items-center pr-6">
                <span>{{ $t("class.set_max_point") }}</span>
              </div>
              <div class="p-0">
                <validation-field
                  :validations="validations.maxPoint"
                  :name="'max point'"
                >
                  <label>{{ $t("class.max_point") }}</label>
                  <md-input
                    required
                    v-model="max_point"
                    :disabled="isDisableAction || parameters.is_default"
                  ></md-input>
                </validation-field>
              </div>
            </div>
            <div
              class="md-layout items-center"
              v-for="(parameter, index) in parameters"
              :key="parameter + index"
            >
              <div
                class="md-layout-item md-size-80 md-xsmall-size-100 md-layout px-0"
              >
                <div class="md-layout-item md-size-50 md-xsmall-size-100 pl-0">
                  <div v-if="parameter.is_default">
                    <div class="text-[#AAAAAA] text-[11px]">
                      {{ $t("class.type_name") }}
                      <span class="text-red-600 pl-[2px]">*</span>
                    </div>
                    <div
                      class="text-[#3c4858] py-[7px] h-8 text-[14px] border-b border-b-[#d2d2d2] font-normal"
                    >
                      {{ $t("class.scores_synced_from_lms_system") }}
                    </div>
                  </div>
                  <validation-field
                    v-else
                    :validations="validations.name"
                    :name="'name ' + `${index + 1}`"
                  >
                    <label>{{ $t("class.type_name") }}</label>
                    <md-input
                      required
                      v-model="parameter.name"
                      :disabled="isDisableAction"
                    ></md-input>
                  </validation-field>
                </div>

                <div class="md-layout-item md-size-50 md-xsmall-size-100">
                  <validation-field
                    :validations="validations.weight"
                    :name="'weight ' + `${index + 1}`"
                  >
                    <label>{{ $t("class.will_calculate_as") }}</label>
                    <md-input
                      v-model="parameter.weight"
                      :disabled="isDisableAction"
                    ></md-input>
                    <span class="md-suffix">%</span>
                  </validation-field>
                </div>
              </div>

              <div
                class="actions md-layout-item md-size-20"
                v-if="isAbleToDelete && !isDisableAction"
              >
                <md-button
                  v-if="index !== 0 && index !== 1"
                  class="md-simple md-just-icon md-icon-button md-danger"
                  @click.native="() => deleteParameter(index)"
                >
                  <md-icon :style="{ fontSize: '30px !important' }">
                    delete_forever
                  </md-icon>
                </md-button>
              </div>
            </div>
            <p
              v-if="!isDisableAction"
              class="text-[#33AE47] font-normal cursor-pointer"
              @click.prevent="addParameter"
            >
              ADD
            </p>
            <p class="text-[14px] pt-2">
              {{ $t("class.set_weights_for_each") }}
            </p>
            <div
              class="md-layout md-gutter pt-4 text-center text-red-500 font-bold text-sm"
              v-if="!parameters.length"
            >
              {{ $t("class.you_must_set_at_least_1_type_in_the_parameter") }}
            </div>
            <div
              class="md-layout md-gutter pt-4 text-center text-red-500 font-bold text-sm"
              v-if="errorMsg"
            >
              {{ errorMsg }}
            </div>
          </div>
          <div class="form-actions" v-if="!isDisableAction">
            <div class="md-layout text-right mr-10">
              <md-button class="md-raised mr-3" @click="handleClose">{{
                $t("common.cancel")
              }}</md-button>
              <div>
                <div>
                  <div class="md-layout relative parameter-lms">
                    <div class="md-layout-item md-size-100 form-confirm">
                      <md-card
                        class="tooltip form-relative w-48 bg-[#f1e8dc] rounded-2xl"
                        :class="{ hidden: !tooltipActive || isSwitchMode }"
                        v-click-outside="closeCardConfirmDelete"
                      >
                        <div class="md-layout">
                          <div
                            class="md-layout-item md-side-100 pt-4 text-center"
                          >
                            {{ $t("class.save_extra_coursework") }}
                          </div>
                          <div class="md-layout-item md-size-100 text-center">
                            <a
                              class="pr-5 text-red-500 cursor-pointer"
                              @click="deleteExtraCoursework"
                              >{{ $t("common.yes") }}</a
                            >
                            <a
                              class="pl-5 text-green-500 cursor-pointer"
                              @click="closeCardConfirmDelete"
                              >{{ $t("common.no") }}</a
                            >
                          </div>
                        </div>
                      </md-card>
                    </div>
                  </div>
                </div>
                <md-button
                  v-if="isSwitchMode"
                  type="submit"
                  class="md-success mr-12"
                >
                  {{ $t("common.save") }}</md-button
                >
                <md-button
                  v-else
                  class="md-success mr-12"
                  @click="confirmDeleteExtraCourseWork"
                >
                  {{ $t("common.save") }}</md-button
                >
              </div>
            </div>
          </div>
        </validation-form>
      </div>
    </template>
  </common-modal>
</template>

<script>
import CommonModal from "@/components/Base/CommonModal";
import { service_paths, service_names } from "@/constants/Services";
import { parameterValidations } from "@/validations/classes";
import cloneDeep from "lodash/cloneDeep";
import Role from "@/constants/Role";
import { hasValidRole } from "@/helpers/role-helper";
import { mapGetters } from "vuex";
import { Message, getId } from "@/helpers/utils";
const defaultParameters = () => {
  return [
    {
      name: "Scores synced from LMS system",
      weight: "",
      is_default: true
    },
    { name: "", weight: "" }
  ];
};
export default {
  components: { CommonModal },
  props: ["name", "title", "isDisableAction"],
  inject: ["getClassInfo"],
  data() {
    const classId = this.$route.params.id;
    return {
      classId: classId,
      path: `${service_paths.class_activity_categories}/class/${classId}`,
      parameters: [],
      parametersTemp: [],
      validations: parameterValidations,
      errorMsg: null,
      Role,
      hasValidRole,
      generateWidth: null,
      isSwitchMode: false,
      tooltipActive: false,
      isConfirmClick: false,
      max_point: null
    };
  },
  computed: {
    isAbleToDelete() {
      return this.parameters.length > 1;
    }
  },
  created() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    closeCardConfirmDelete() {
      if (this.isConfirmClick) {
        this.isConfirmClick = false;
        return;
      }
      this.tooltipActive = false;
    },
    confirmDeleteExtraCourseWork() {
      this.isConfirmClick = true;
      this.tooltipActive = !this.tooltipActive;
    },
    setMode(classInfo) {
      if (classInfo.class_activity_categories.length) {
        this.isSwitchMode = true;
        return;
      }
      this.isSwitchMode = false;
    },
    handleResize() {
      this.generateWidth = window.innerWidth < 959 ? "95%" : "800px";
    },
    getParameters() {
      return this.$axios.get(this.path).then(response => {
        if (response.data) {
          this.setupParameter(response.data);
        }
      });
    },
    setupParameter(data) {
      this.parameters = data;
      this.max_point = this.parameters[0]?.max_point;
      this.parametersTemp = cloneDeep(data);
      if (!data.length) {
        this.parameters = defaultParameters();
      }
      if (data.length === 1) {
        this.addParameter();
      }
    },
    addParameter() {
      this.parameters.push({ name: "", weight: "" });
    },
    deleteParameter(index) {
      this.parameters.splice(index, 1);
    },
    deleteExtraCoursework() {
      const path = `${service_names.school}/class-activities/lms/setup-parameter/class/${this.classId}`;
      this.$axios.delete(path).then(() => {
        this.getClassInfo();
        this.$modal.hide(this.name);
      });
    },
    saveParameters() {
      const parameters = this.buildParameter();
      if (!parameters) return;
      this.save(parameters);
    },
    buildParameter() {
      const reducer = (previousValue, currentValue) =>
        +previousValue + +currentValue.weight;
      const sum = this.parameters.reduce(reducer, 0);
      if (sum > 100) {
        this.errorMsg = this.$t("class.total_weight_not_be_more_than_100");
        return;
      }
      this.errorMsg = null;
      return this.parameters.map(item => {
        return {
          name: item.name,
          id: getId(item) || "",
          weight: item.weight,
          max_point: this.max_point
        };
      });
    },
    save(parameters) {
      this.$axios
        .post(
          `${service_names.school}/class-activities/lms/setup-parameter/class/${this.classId}`,
          parameters
        )
        .then(() => {
          Message.success(this.$t("common.success"));
          this.getClassInfo();
          this.$modal.hide(this.name);
        });
    },

    handleClose() {
      this.$modal.hide(this.name);
      this.parameters = cloneDeep(this.parametersTemp);
    }
  }
};
</script>

<style lang="scss">
.parameter-lms {
  .form-confirm {
    height: 0;
    position: absolute;
    top: 10px;
    left: 0;
    .form-relative {
      top: -145px !important;
      left: -65px !important;
    }
  }
}
</style>
